import { Box } from '@mui/material';
import { lazy, Suspense } from 'react';
import type { CalendarChartConfig, DataPoint } from '@/features/chartsView';
import {
  isRequestedConfigEqual,
  LineChartWrapper,
  useFetchData,
  useLineChartParams,
} from '@/features/chartsView';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Loader } from '@/shared/ui';
import { CHART_BASE_DIMENSION } from '../../model/constants';
import {
  getChartData,
  selectMeta,
  selectChart,
  selectChartData,
  isRequestedConfigExist,
  selectSplitDimensionIsMedia,
  selectDimensionIsDate,
} from '../../model/slice';

const LineChart = lazy(() =>
  import('@/features/chartsView').then(({ LineChart }) => {
    return {
      default: LineChart<typeof CHART_BASE_DIMENSION>,
    };
  }),
);

export function Chart() {
  const dispatch = useAppDispatch();
  const calendarMeta = useAppSelector(selectMeta);
  const chart = useAppSelector(selectChart) as CalendarChartConfig;
  const chartData = useAppSelector(selectChartData);
  const isMediaContent = useAppSelector(selectSplitDimensionIsMedia);
  const isDateContent = useAppSelector(selectDimensionIsDate);
  const splitOptions =
    chartData.data?.splitOptions?.[chart.splitDimension] || [];
  const { noData, lines, leftY, rightY, data } = useLineChartParams({
    meta: calendarMeta,
    chart,
    data: (chartData?.data?.data || []) as DataPoint<
      typeof CHART_BASE_DIMENSION
    >[],
    splitOptions,
    defaultSplitOption: CHART_BASE_DIMENSION,
    excludedSplitOptions: chart.excludedSplitOptions,
  });

  useFetchData({
    isDataAndCurrentConfigEqual: isRequestedConfigEqual(
      isRequestedConfigExist(chartData) ? chartData.requestedConfig : null,
      chart,
    ),
    onGetData: () => dispatch(getChartData(chart)),
  });

  return (
    <Box sx={{ flexGrow: 1 }}>
      <LineChartWrapper
        dataStatus={chartData.status}
        noData={noData}
        noSelection={lines.length === chart.excludedSplitOptions.length}
      >
        <Suspense fallback={<Loader active />}>
          <LineChart
            sx={{ border: 'none' }}
            syncYAxesDomains={chart.syncedYAxes}
            excludedLines={chart.excludedSplitOptions}
            isMediaContent={isMediaContent}
            isDateContent={isDateContent}
            xAxisDataKey={CHART_BASE_DIMENSION}
            leftY={leftY}
            rightY={rightY}
            data={data}
            lines={lines}
          />
        </Suspense>
      </LineChartWrapper>
    </Box>
  );
}
